import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { def } from "../../../../config";
import TextInput from "../../../../components/form/TextInput";
import CitiesInput from "../../../../components/form/CitiesInput";
import { CircularProgress, MenuItem } from "@mui/material";
import Button1 from "../../../../components/form/Button1";
import createFlightPriceAdjustment from "../../../../controllers/flightPriceAdjustment/createFlightPriceAdjustment";
import updateFlightPriceAdjustment from "../../../../controllers/flightPriceAdjustment/updateFlightPriceAdjustment";
import AirlinesInput from "../../../../components/form/AirlinesInput";
import ToolTip from "../../../../components/DIsplay/ToolTip";
import Checkbox from "../../../../components/form/Checkbox";
import filterTruthyValues, {
  filterOutNullValues,
} from "../../../../features/utils/filterTruthyObjectValues";
import Icon from "../../../../components/HOC/Icon";
import RadioGroup from "../../../../components/form/RadioGroup";
import createOrderPriceAdjustments from "../../../../controllers/orderPriceAdjustment/createOrderPriceAdjustment";
import ListAirlines from "./ListAirlines";
import { flightSuppliers } from "../../../../data/ENUM/FlightProviders";
import updateOrderPriceAdjustments from "../../../../controllers/orderPriceAdjustment/updateOrderPriceAdjustment";
import getSuppliersList from "../../../../controllers/settings/supplier/getSuppliersList";
import getTaxes from "../../../../controllers/Tax/getTaxes";
import getIntraverseCommissions from "../../../../controllers/SplitCommission/getIntraverseCommissions";
import getHotelSettings from "../../../../controllers/hotelSettings/getHotelSettings";
import useAccount from "../../../../hooks/useAccount";
import Unauthorized from "../../../../components/DIsplay/Unauthorized";
import createPostticketingPriceAdjustment from "../../../../controllers/postTicketingPriceAdjustment/createFlightPriceAdjustment";
import updatePostticketingPriceAdjustment from "../../../../controllers/postTicketingPriceAdjustment/updateFlightPriceAdjustment";
import { LocalAirports } from "../../../../data/flight/airports";
import createGuestPriceAdjustment from "../../../../controllers/flightPriceAdjustment/createGuestPriceAdjustment";
import updateGuestPriceAdjustment from "../../../../controllers/flightPriceAdjustment/updateGuestPriceAdjustment";

/** Matches backend `AGENT_TYPE` enum */
const AGENT_TYPE_OPTIONS = [
  "Business",
  "Independent",
  "Corporate",
  "Developer",
  "Explorer",
  "Unassigned",
];

function normalizeStringArray(val) {
  if (Array.isArray(val))
    return val.map((v) => String(v).trim()).filter(Boolean);
  if (val == null || val === "") return [];
  return [String(val).trim()].filter(Boolean);
}

function uniqueNonEmptyStrings(arr) {
  const list = normalizeStringArray(arr);
  return [...new Set(list)];
}

/** API stores tax as an array; UI allows a single id */
function normalizeTaxSelection(tax) {
  if (tax == null || tax === "") return undefined;
  const arr = Array.isArray(tax) ? tax : [tax];
  const id = arr.filter(Boolean)[0];
  return id ? [id] : undefined;
}

function getSelectedTaxId(tax) {
  const normalized = normalizeTaxSelection(tax);
  return normalized?.[0] ?? "";
}

const initData = {
  appliedTo: "Flight",
  method: "Percentage",
  airlines: [],
  excludedAirlines: [],
  excludedAirline: undefined,
  type: "Markup",
  amount: 0,
  name: "",
  minPrice: undefined,
  maxPrice: undefined,
  currency: undefined,
  passengerType: undefined,
  flightRoute: undefined,
  airline: undefined,
  departureAirport: undefined,
  excludedDepartureAirports: [],
  arrivalAirport: undefined,
  transitAirport: undefined,
  cabinClass: undefined,
  ticketClass: [],
  excludedTicketClass: [],
  agentType: undefined,
  fareBasis: [],
  excludedFareBasis: [],
  arrivalTime: undefined,
  departureTime: undefined,
  tier: [{ above: 0, amount: null }],
  tax: undefined,
  intraverseCommission: undefined,
  fareType: undefined,
  priceType: null,
};

const PRICE_TYPE_OPTIONS = [
  { value: "", label: "None" },
  { value: "NDC", label: "NDC" },
  { value: "GDS", label: "GDS" },
];

export default function CreateMarkup({
  reload,
  footer,
  forType,
  subType,
  data: defData,
  update,
  hideMoreOptions,
  override,
}) {
  function normalizeFareType(fareType) {
    if (Array.isArray(fareType)) return fareType?.[0] || "";
    return fareType || "";
  }

  function addTier(obj) {
    if (!obj?.tier?.length) return initData?.tier;
    else return obj?.tier;
  }
  const [data, setData] = useState(
    {
      ...initData,
      ...(defData || {}),
      tier: forType === "Stays" ? addTier(defData) : undefined,
      fareType: normalizeFareType(defData?.fareType),
      ticketClass: normalizeStringArray(
        defData?.ticketClass ?? initData.ticketClass,
      ),
      excludedTicketClass: normalizeStringArray(
        defData?.excludedTicketClass ?? initData.excludedTicketClass,
      ),
      fareBasis: normalizeStringArray(defData?.fareBasis ?? initData.fareBasis),
      excludedFareBasis: normalizeStringArray(
        defData?.excludedFareBasis ?? initData.excludedFareBasis,
      ),
      agentType: defData?.agentType ?? initData.agentType,
      tax: normalizeTaxSelection(defData?.tax),
      priceType: ["NDC", "GDS"].includes(defData?.priceType)
        ? defData.priceType
        : null,
    } || initData,
  );
  const [taxes, setTaxes] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loadings, setLoadings] = useState({ submit: false, suppliers: false });
  const [suppliers, setSuppliers] = useState([]);
  const [settings, setSettings] = useState();
  const { checkPermission } = useAccount();

  const hotelSuppliers = Object.entries(settings?.hotelSuppliers || {})?.map(
    ([name]) => ({
      name,
    }),
  );
  const insuranceSuppliers = Object.entries(
    settings?.insuranceSuppliers || {},
  )?.map(([name]) => ({
    name,
  }));
  const packageSuppliers = Object.entries(
    settings?.packageSuppliers || {},
  )?.map(([name]) => ({
    name,
  }));

  const { enqueueSnackbar } = useSnackbar();

  let currency = def.currencyCode;

  useEffect(() => {
    if (forType === "Flights") {
      loadSuppliers();
      loadTaxes();
      loadIntraverseCommission();
    }
    loadSettings();
    //eslint-disable-next-line
  }, []);

  // After taxes load, drop selection if tax was deleted from the catalog
  useEffect(() => {
    if (forType !== "Flights" || loadings?.taxes) return;

    const selectedId = getSelectedTaxId(data?.tax);
    if (!selectedId) return;

    const exists = taxes?.some((t) => t?._id === selectedId);
    if (!exists) {
      setData((prev) => ({ ...prev, tax: undefined }));
    }
    //eslint-disable-next-line
  }, [taxes, loadings?.taxes, forType]);

  async function loadSuppliers() {
    setLoadings({ ...loadings, suppliers: true });
    const res = await getSuppliersList();
    setLoadings({ ...loadings, suppliers: false });
    if (res?.return) {
      setSuppliers(res?.data);
    } else {
      enqueueSnackbar(res?.msg, { variant: "error" });
    }
  }

  async function loadTaxes() {
    setLoadings({ ...loadings, taxes: true });
    const res = await getTaxes();
    setLoadings({ ...loadings, taxes: false });
    if (res?.return) {
      const fetchedTaxes = res?.data?.data;
      if (!fetchedTaxes?.length) {
        setData({ ...data, tax: [] });
      } else {
        const selectedTax = data?.tax?.filter((tax) => {
          const foundTax = fetchedTaxes?.find((t) => t?._id === tax);

          return Boolean(foundTax);
        });

        setData({ ...data, tax: selectedTax });
      }
      setTaxes(fetchedTaxes);
    }
  }
  async function loadIntraverseCommission() {
    setLoadings({ ...loadings, intraverseCommission: true });
    const res = await getIntraverseCommissions();
    setLoadings({ ...loadings, intraverseCommission: false });
    if (res?.return) {
      setCommissions(res?.data?.data);
    }
  }
  async function loadSettings() {
    setLoadings({ ...loadings, settings: true });
    const res = await getHotelSettings();
    setLoadings({ ...loadings, settings: false });
    if (res?.return) {
      setSettings(res?.data);
    }
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    if (data.appliedTo === "" || data.method === "")
      return enqueueSnackbar("Please fill required fields!", {
        variant: "error",
      });

    setLoadings({ ...loadings, submit: true });
    let res = { return: false, msg: "Error", data: [] };
    // delete data.ticketClass;
    let { _id, createdAt, updatedAt, id, ...modData } = filterTruthyValues({
      ...data,
      fareType: data?.fareType
        ? [Array.isArray(data?.fareType) ? data?.fareType?.[0] : data?.fareType]
        : undefined,
      ticketClass: (() => {
        const letters = uniqueNonEmptyStrings(data?.ticketClass)
          .map((s) =>
            s
              .toUpperCase()
              .replace(/[^A-Z]/g, "")
              .slice(0, 1),
          )
          .filter(Boolean);
        return letters.length ? [...new Set(letters)] : [];
      })(),
      excludedTicketClass: (() => {
        const letters = uniqueNonEmptyStrings(data?.excludedTicketClass)
          .map((s) =>
            s
              .toUpperCase()
              .replace(/[^A-Z]/g, "")
              .slice(0, 1),
          )
          .filter(Boolean);
        return letters.length ? [...new Set(letters)] : [];
      })(),
      fareBasis: (() => {
        const codes = uniqueNonEmptyStrings(data?.fareBasis).map((s) =>
          s.toUpperCase(),
        );
        return codes.length ? codes : [];
      })(),
      excludedFareBasis: (() => {
        const codes = uniqueNonEmptyStrings(data?.excludedFareBasis).map((s) =>
          s.toUpperCase(),
        );
        return codes.length ? codes : [];
      })(),
      agentType: data?.agentType || undefined,
      amount: (() => {
        const raw = parseFloat(data?.amount);
        if (data?.method === "Percentage" && !Number.isNaN(raw)) {
          return Math.min(100, Math.max(0, raw));
        }
        return raw;
      })(),
      departureAirport: data?.departureAirport?.iata || data?.departureAirport,
      arrivalAirport: data?.arrivalAirport?.iata || data?.arrivalAirport,
      tier:
        data?.method === "Fixed"
          ? undefined
          : data?.tier?.map((obj) => ({
              amount: Number(obj?.amount || 0),
              above: Number(obj?.above || 0),
            })),
    });
    if (!modData?.minCap) {
      modData.minCap = null;
    }
    if (!modData?.maxCap) {
      modData.maxCap = null;
    }

    // filterTruthyValues drops undefined; explicit null so clearing commission persists on save
    if (forType === "Flights") {
      modData.intraverseCommission = data?.intraverseCommission ?? null;
      modData.priceType = ["NDC", "GDS"].includes(data?.priceType)
        ? data.priceType
        : null;
      if (override && forType === "Flights") {
        modData.override = true;
      }
    }

    if (forType === "Stays") modData["appliedTo"] = "Hotel";
    else if (forType === "Tours") modData["appliedTo"] = "Package";
    else if (forType === "Insurance") modData["appliedTo"] = "Insurance";

    if (["Stays", "Tours", "Insurance"]?.includes(forType)) {
      if (update)
        res = await updateOrderPriceAdjustments(data?._id, { ...modData });
      else res = await createOrderPriceAdjustments({ ...modData });
    } else if (!update)
      if (subType === "Post Ticketing")
        res = await createPostticketingPriceAdjustment({ ...modData });
      else if (subType === "Guest")
        res = await createGuestPriceAdjustment({ ...modData });
      else res = await createFlightPriceAdjustment({ ...modData });
    else {
      if (subType === "Post Ticketing")
        res = await updatePostticketingPriceAdjustment(data?._id, {
          ...modData,
        });
      else if (subType === "Guest")
        res = await updateGuestPriceAdjustment(data?._id, {
          ...modData,
        });
      else res = await updateFlightPriceAdjustment(data?._id, { ...modData });
    }

    setLoadings({ ...loadings, submit: false });
    if (res.return) {
      if (!update) setData(defData || initData);
      enqueueSnackbar(update ? "Markup updated" : "Markup created.", {
        variant: "success",
      });
      reload && reload();
    } else enqueueSnackbar(res.msg, { variant: "error" });
  }

  useEffect(() => {
    setData({
      ...initData,
      ...(defData || {}),
      fareType: Array.isArray(defData?.fareType)
        ? defData?.fareType?.[0]
        : defData?.fareType,
      ticketClass: normalizeStringArray(
        defData?.ticketClass ?? initData.ticketClass,
      ),
      excludedTicketClass: normalizeStringArray(
        defData?.excludedTicketClass ?? initData.excludedTicketClass,
      ),
      fareBasis: normalizeStringArray(defData?.fareBasis ?? initData.fareBasis),
      excludedFareBasis: normalizeStringArray(
        defData?.excludedFareBasis ?? initData.excludedFareBasis,
      ),
      agentType: defData?.agentType ?? initData.agentType,
      tax: normalizeTaxSelection(defData?.tax),
      priceType: ["NDC", "GDS"].includes(defData?.priceType)
        ? defData.priceType
        : null,
      tier: forType === "Stays" ? addTier(defData) : undefined,
    });
  }, [defData, forType]);

  const taxSelectValue = useMemo(() => {
    if (loadings?.taxes) return "";
    const id = getSelectedTaxId(data?.tax);
    if (!id) return "";
    return taxes?.some((t) => t?._id === id) ? id : "";
  }, [loadings?.taxes, data?.tax, taxes]);

  // console.log(data);
  const [allAirline, setAllAirline] = useState("All");

  useEffect(() => {
    if (allAirline === "All") setData((prev) => ({ ...prev, airline: "All" }));
    else setData((prev) => ({ ...prev, airline: undefined }));
    //eslint-disable-next-line
  }, [allAirline]);

  function handleTier(obj, i) {
    try {
      const modData = { ...data };
      modData.tier[i] = { ...(modData.tier[i] || {}), ...(obj || {}) };
      setData(modData);
    } catch (ex) {}
  }
  function handleAddTier() {
    try {
      const modData = { ...data };
      modData.tier.push({ above: null, amount: null });
      setData(modData);
    } catch (ex) {}
  }

  if (
    !checkPermission([
      "update:flight-price-adjustment",
      "create:flight-price-adjustment",
    ])
  )
    return <Unauthorized />;

  // console.log({ data });

  return (
    <div className="w-[90vw] lg:w-fit lg:min-w-[740px]">
      <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
        {!hideMoreOptions && (
          <h5>
            {update
              ? "Update Markup"
              : "Create a new Markup for " + (subType || forType)}
          </h5>
        )}

        {subType === "Post Ticketing" ? (
          <div>
            <TextInput
              select
              label="Type"
              value={data?.postTicketAction || ""}
              onChange={(ev) => {
                const updatedData = {
                  ...data,
                  postTicketAction: ev?.target?.value,
                };
                switch (ev?.target?.value) {
                  case "Refund":
                    updatedData.type = "Markdown";
                    break;
                  case "ChangeFlight":
                    updatedData.type = "Markup";
                    break;
                }
                if (ev?.target?.value) setData(updatedData);
              }}
            >
              <MenuItem value="Refund">Refund</MenuItem>
              <MenuItem value="Void">Void</MenuItem>
              <MenuItem value="ChangeFlight">Change Flight</MenuItem>
              <MenuItem value="ChangePassenger">Change Passenger</MenuItem>
            </TextInput>
          </div>
        ) : null}

        <span className="w-full text-sm text-gray-500 -mb-2">Markup title</span>
        <TextInput
          placeholder={
            forType === "Flights"
              ? "e.g Virgin Air Markup"
              : "e.g Holiday Markup"
          }
          required
          value={data?.name}
          onChange={(ev) => setData({ ...data, name: ev.target.value })}
        />

        {/* {forType === "Flights" ? (
          <>
            <TextInput
              label="Fare Type"
              select
              // required
              SelectProps={{ multiple: true }}
              value={data?.fareType || []}
              onChange={(ev) =>
                setData({ ...data, fareType: ev?.target?.value })
              }
            >
              {["Published", "Negotiated", "Corporate"]?.map((obj, i) => (
                <MenuItem key={i} value={obj}>
                  {obj}
                </MenuItem>
              ))}
            </TextInput>
          </>
        ) : (
          <></>
        )} */}

        {forType === "Flights" ? (
          // {forType === "Flights" && subType !== "Post Ticketing" ? (
          <>
            <div>
              <small>Fare Type</small>
              <div className="flex gap-2">
                <Checkbox
                  labelClassName="w-full"
                  name="Published"
                  value="Published"
                  checked={data?.fareType === "Published"}
                  onChange={(e) => {
                    setData({
                      ...data,
                      fareType: e.target.checked ? "Published" : "",
                    });
                  }}
                >
                  Published
                </Checkbox>
                <Checkbox
                  labelClassName="w-full"
                  name="Negotiated"
                  value="Negotiated"
                  checked={data?.fareType === "Negotiated"}
                  onChange={(e) => {
                    setData({
                      ...data,
                      fareType: e.target.checked ? "Negotiated" : "",
                    });
                  }}
                >
                  Negotiated
                </Checkbox>
                <Checkbox
                  labelClassName="w-full"
                  name="Corporate"
                  value="Corporate"
                  checked={data?.fareType === "Corporate"}
                  onChange={(e) => {
                    setData({
                      ...data,
                      fareType: e.target.checked ? "Corporate" : "",
                    });
                  }}
                >
                  Corporate
                </Checkbox>
              </div>
            </div>
            <div>
              <small>Price type</small>
              <div className="flex flex-wrap gap-2 pt-1">
                {PRICE_TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value || "none"}
                    type="button"
                    className={
                      (data?.priceType ?? "") === opt.value
                        ? "btn"
                        : "rounded-md px-4 py-2 bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200 capitalize cursor-pointer font-normal"
                    }
                    onClick={() =>
                      setData({
                        ...data,
                        priceType: opt.value || null,
                      })
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <TextInput
              select
              label="Agent type"
              value={data?.agentType}
              onChange={(ev) =>
                setData({
                  ...data,
                  agentType: ev.target.value || undefined,
                })
              }
            >
              <MenuItem value={undefined}>Any</MenuItem>
              {AGENT_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextInput>
          </>
        ) : null}

        {forType === "Flights" ? (
          <div className="self-start flex flex-col items-center gap-4 justify-between w-full">
            <span className="w-full text-sm text-gray-500 -mb-3">
              Applicable to
            </span>
            <div className="w-full">
              <RadioGroup
                value={allAirline}
                options={[
                  { value: "All", label: "All Airline" },
                  { value: "Specific", label: "Specific Airlines" },
                ]}
                className="flex gap-4 whitespace-nowrap w-full"
                onChange={(val) => setAllAirline(val)}
              />
              {/* <Checkbox value={allAirline} onChange={(ev) => ev.target.checked && setData({...data,airline: 'ALL'}) && setAllAirline(ev.target.checked)}>
              All Airlines
            </Checkbox> */}
            </div>
            {allAirline === "Specific" ? (
              <div className="w-full">
                <AirlinesInput
                  label={"Airline"}
                  placeholder="Search or select airline"
                  // disabled={allAirline}
                  val={data?.airline}
                  returnData={(val) => {
                    if (val?.id)
                      setData({
                        ...data,
                        airline: "",
                        // airline: val?.id,
                        airlines: [...(data?.airlines || []), val?.id],
                      });
                  }}
                />
                <div className="py-2">
                  <ListAirlines
                    list={data?.airlines}
                    setList={(airlines) => setData({ ...data, airlines })}
                  />
                </div>
              </div>
            ) : null}

            <div className="w-full">
              <AirlinesInput
                label={"Excluded Airlines"}
                placeholder="Search or select airline"
                // disabled={allAirline}
                val={data?.excludedAirline}
                returnData={(val) => {
                  if (val?.id)
                    setData({
                      ...data,
                      excludedAirline: "",
                      // airline: val?.id,
                      excludedAirlines: [
                        ...(data?.excludedAirlines || []),
                        val?.id,
                      ],
                    });
                }}
              />
              <div className="py-2">
                <ListAirlines
                  list={data?.excludedAirlines}
                  setList={(excludedAirlines) =>
                    setData({ ...data, excludedAirlines })
                  }
                />
              </div>
            </div>
          </div>
        ) : null}

        {forType === "Flights" ? (
          <TextInput
            label="Suppliers"
            select
            required
            SelectProps={{ multiple: true }}
            value={data?.suppliers || []}
            onChange={(ev) =>
              setData({ ...data, suppliers: ev?.target?.value })
            }
            InputProps={{
              endAdornment: (
                <span className="text-primary/50 font-light mr-6 flex items-center">
                  {loadings?.suppliers ? (
                    <CircularProgress size={18} className="!text-theme1" />
                  ) : null}
                </span>
              ),
            }}
          >
            {suppliers?.map((obj, i) => (
              <MenuItem key={i} value={obj.value}>
                {obj?.value}
              </MenuItem>
            ))}
          </TextInput>
        ) : forType === "Stays" ? (
          <TextInput
            label="Suppliers"
            select
            required
            SelectProps={{ multiple: true }}
            value={data?.hotelSuppliers || []}
            onChange={(ev) =>
              setData({ ...data, hotelSuppliers: ev?.target?.value })
            }
            InputProps={{
              endAdornment: (
                <span className="text-primary/50 font-light mr-6 flex items-center">
                  {loadings?.settings ? (
                    <CircularProgress size={18} className="!text-theme1" />
                  ) : null}
                </span>
              ),
            }}
          >
            {hotelSuppliers?.map((obj, i) => (
              <MenuItem key={i} value={obj.name}>
                {obj?.name}
              </MenuItem>
            ))}
          </TextInput>
        ) : forType === "Tours" ? (
          <TextInput
            label="Suppliers"
            select
            required
            SelectProps={{ multiple: true }}
            value={data?.packageSuppliers || []}
            onChange={(ev) =>
              setData({ ...data, packageSuppliers: ev?.target?.value })
            }
            InputProps={{
              endAdornment: (
                <span className="text-primary/50 font-light mr-6 flex items-center">
                  {loadings?.settings ? (
                    <CircularProgress size={18} className="!text-theme1" />
                  ) : null}
                </span>
              ),
            }}
          >
            {packageSuppliers?.map((obj, i) => (
              <MenuItem key={i} value={obj.name}>
                {obj?.name}
              </MenuItem>
            ))}
          </TextInput>
        ) : forType === "Insurance" ? (
          <TextInput
            label="Suppliers"
            select
            required
            SelectProps={{ multiple: true }}
            value={data?.insuranceSuppliers || []}
            onChange={(ev) =>
              setData({ ...data, insuranceSuppliers: ev?.target?.value })
            }
            InputProps={{
              endAdornment: (
                <span className="text-primary/50 font-light mr-6 flex items-center">
                  {loadings?.settings ? (
                    <CircularProgress size={18} className="!text-theme1" />
                  ) : null}
                </span>
              ),
            }}
          >
            {insuranceSuppliers?.map((obj, i) => (
              <MenuItem key={i} value={obj.name}>
                {obj?.name}
              </MenuItem>
            ))}
          </TextInput>
        ) : null}

        {/* <p>Select product to markup</p> */}
        {/* <div className='flex flex-wrap gap-4 justify-between self-stretch pb-4'>
            <Checkbox labelClassName='flex-1' name='type' checked={data.appliedTo.includes('Flight')} onChange={handleChange} value='Flight'>Flight</Checkbox>
            <Checkbox labelClassName='flex-1' name='type' checked={data.appliedTo.includes('Stay')} onChange={handleChange} value='Stay'>Stay</Checkbox>
            <Checkbox labelClassName='flex-1' name='type' checked={data.appliedTo.includes('Tour')} onChange={handleChange} value='Tour'>Tour</Checkbox>
            <Checkbox labelClassName='' name='type' checked={data.appliedTo.includes('Protection')} onChange={handleChange} value='Protection'>Protection</Checkbox>
          </div> */}

        {/* <div className="self-start">
          <AirlinesInput
            label={"Airline"}
            placeholder="Search or select airline"
            val={data?.airline}
            returnData={(val) => {
              setData({
                ...data,
                airline: val?.id,
              });
            }}
          />
        </div> */}

        <div className="flex flex-row gap-2">
          <TextInput
            select
            required
            value={data?.type || ""}
            label="Type"
            onChange={(e) => setData({ ...data, type: e.target.value })}
          >
            <MenuItem value="Markup">Mark Up</MenuItem>
            <MenuItem value="Markdown">Mark Down</MenuItem>
          </TextInput>
          <TextInput
            select
            required
            value={data?.method || ""}
            label="Markup Method"
            onChange={(e) => {
              const method = e.target.value;
              const next = { ...data, method };
              if (
                method === "Percentage" &&
                data?.amount !== "" &&
                data?.amount != null &&
                Number(data.amount) > 100
              ) {
                next.amount = Math.min(100, Number(data.amount));
              }
              setData(next);
            }}
          >
            <MenuItem value="Fixed">Fixed</MenuItem>
            <MenuItem value="Percentage">Percentage</MenuItem>
          </TextInput>

          {/* <small>Method</small>
          <RadioGroup
            name="method"
            required
            value={data.method}
            onChange={(ev) => setData({ ...data, method: ev.target.value })}
            className="flex flex-col gap-2"
          >
            <RadioInput value="Value" checked={data.method === "Value"}>
              <div className="flex flex-col ">
                <h6>Value</h6>
                <p>
                  This will add the amount you choose to any ticket you want to
                  sell
                </p>
              </div>
            </RadioInput>
            <RadioInput value="Percentage" checked={data.method === "Percentage"}>
              <div className="flex flex-col ">
                <h6>Percentage</h6>
                <p>
                  This will add the percentage you choose to any ticket you want
                  to sell
                </p>
              </div>
            </RadioInput>
          </RadioGroup> */}
        </div>
        <div className="flex gap-2">
          {forType === "Flights" ? (
            <TextInput
              select
              required
              value={data?.currency || ""}
              label="Currency"
              onChange={(e) => setData({ ...data, currency: e.target.value })}
            >
              <MenuItem value="NGN">NGN</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </TextInput>
          ) : null}
          <TextInput
            type="number"
            required
            value={data?.amount ?? ""}
            label="Value"
            onChange={(e) => {
              const val = e.target.value;

              // Allow empty string (for editing) and "0"
              if (val === "") {
                setData({ ...data, amount: "" });
                return;
              }

              let num = Number(val);

              // Still enforce non-negative
              num = Math.max(0, num);
              if (data?.method === "Percentage") {
                num = Math.min(100, num);
              }

              setData({ ...data, amount: num });
            }}
          />
        </div>
        <div className="flex gap-2">
          <TextInput
            value={data?.minPrice || ""}
            label="Price Range from"
            onChange={(e) =>
              setData({ ...data, minPrice: Number(e.target.value) })
            }
          />
          <TextInput
            value={data?.maxPrice || ""}
            label="Price Range To"
            onChange={(e) =>
              setData({ ...data, maxPrice: Number(e.target.value) })
            }
          />
        </div>

        {/* Tax and Commission*/}

        {forType === "Flights" ? (
          <>
            <div className="flex gap-4 items-center">
              <TextInput
                label="Tax"
                select
                value={taxSelectValue}
                onChange={(ev) => {
                  const id = ev.target.value;
                  setData({
                    ...data,
                    tax: id ? [id] : undefined,
                  });
                }}
                disabled={loadings?.taxes}
                InputProps={{
                  endAdornment: (
                    <span className="text-primary/50 font-light mr-6 flex items-center">
                      {loadings?.taxes ? (
                        <CircularProgress size={18} className="!text-theme1" />
                      ) : null}
                    </span>
                  ),
                }}
              >
                <MenuItem value="">
                  <em className="text-[#A4B1C1]">None</em>
                </MenuItem>
                {taxes?.map((obj, i) => (
                  <MenuItem key={i} value={obj?._id}>
                    <div className="flex items-center justify-between w-full">
                      {obj?.taxName}
                      <span>
                        {obj?.taxAmount}{" "}
                        {obj?.taxMethod === "Percentage"
                          ? "%"
                          : def?.currencyCode}
                      </span>
                    </div>
                  </MenuItem>
                ))}
              </TextInput>
              <TextInput
                label="Commision"
                select
                value={data?.intraverseCommission ?? ""}
                onChange={(ev) =>
                  setData({
                    ...data,
                    intraverseCommission: ev.target.value || undefined,
                  })
                }
                // SelectProps={{ multiple: true }}
                InputProps={{
                  endAdornment: (
                    <span className="text-primary/50 font-light mr-6 flex items-center">
                      {loadings?.intraverseCommission ? (
                        <CircularProgress size={18} className="!text-theme1" />
                      ) : null}
                    </span>
                  ),
                }}
              >
                <MenuItem value="">
                  <em className="text-[#A4B1C1]">None</em>
                </MenuItem>
                {commissions?.map((obj, i) => (
                  <MenuItem key={i} value={obj?._id}>
                    <div className="flex items-center justify-between w-full">
                      {obj?.name}
                      <span>
                        {obj?.commission}{" "}
                        {obj?.method === "Percentage" ? "%" : def?.currencyCode}
                      </span>
                    </div>
                  </MenuItem>
                ))}
              </TextInput>
            </div>
            <div className="flex gap-2">
              <TextInput
                value={data?.minCap || ""}
                label="Min Capacity"
                onChange={(e) =>
                  setData({ ...data, minCap: Number(e.target.value) })
                }
              />
              <TextInput
                value={data?.maxCap || ""}
                label="Max Capacity"
                onChange={(e) =>
                  setData({ ...data, maxCap: Number(e.target.value) })
                }
              />
            </div>
          </>
        ) : null}

        {forType === "Flights" && subType !== "Post Ticketing" ? (
          <div>
            <small>Applied To *</small>
            <div className="flex gap-2">
              <Checkbox
                labelClassName="w-full"
                name="flight"
                value="Flight"
                checked={data?.appliedTo === "Flight"}
                onChange={(e) => {
                  setData({
                    ...data,
                    appliedTo: e.target.checked ? "Flight" : "Ancillary",
                  });
                }}
              >
                Flight
              </Checkbox>
              <Checkbox
                labelClassName="w-full"
                name="Ancillary"
                value="Ancillary"
                checked={data?.appliedTo === "Ancillary"}
                onChange={(e) => {
                  setData({
                    ...data,
                    appliedTo: e.target.checked ? "Ancillary" : "Flight",
                  });
                }}
              >
                Ancillaries
              </Checkbox>
            </div>
          </div>
        ) : null}

        {forType === "Stays" && data?.tier?.length ? (
          <div className="flex flex-col gap-4">
            <h5>Tier</h5>
            {data?.tier?.map((obj, i) => (
              <div key={i} className="flex gap-4 items-center">
                <TextInput
                  label="Price Above"
                  type="number"
                  min={0}
                  value={obj?.above}
                  disabled={i === 0}
                  onChange={(ev) =>
                    handleTier({ above: ev.target.value || 0 }, i)
                  }
                />
                <TextInput
                  label="Amount"
                  type="number"
                  value={obj?.amount}
                  onChange={(ev) => handleTier({ amount: ev.target.value }, i)}
                />
                {i === data?.tier?.length - 1 ? (
                  <button
                    className="bg-secondary shadow-top rounded-full p-1 w-[50px]"
                    onClick={() => handleAddTier()}
                  >
                    <Icon icon="mdi:add" />
                  </button>
                ) : (
                  <div className="w-[50px] p-2"></div>
                )}
              </div>
            ))}
          </div>
        ) : null}

        {!hideMoreOptions && (
          <>
            <div>
              <h5>More Options</h5>
              <ToolTip>Leave blank if applied to all</ToolTip>
            </div>
            <div className="flex gap-4">
              {forType === "Stays" ? (
                <CitiesInput placeholder="DXB" label="City Code" />
              ) : null}
              {/* <TextInput
              required
              label={"Figure"}
              placeholder={"e.g 24,000"}
              InputProps={{ endAdornment: currency }}
              value={data.amount}
              onChange={(ev) => setData({ ...data, amount: ev.target.value })}
            /> */}
            </div>
            {forType === "Flights" ? (
              <div className="flex flex-col gap-4">
                <div>
                  <div>
                    <small>Passenger Type</small>
                    <div className="flex flex-wrap gap-4 justify-between self-stretch pb-4">
                      <Checkbox
                        labelClassName="flex-1"
                        name="type"
                        checked={data?.passengerType === "Adult"}
                        onChange={(e) => {
                          setData({
                            ...data,
                            passengerType: e.target.checked ? "Adult" : "",
                          });
                        }}
                        value="Adult"
                      >
                        Adult
                      </Checkbox>
                      <Checkbox
                        labelClassName="flex-1"
                        name="type"
                        checked={data?.passengerType === "Child"}
                        onChange={(e) => {
                          setData({
                            ...data,
                            passengerType: e.target.checked ? "Child" : "",
                          });
                        }}
                        value="Child"
                      >
                        Child
                      </Checkbox>
                      <Checkbox
                        labelClassName="flex-1"
                        name="type"
                        checked={data?.passengerType === "Infant"}
                        onChange={(e) => {
                          setData({
                            ...data,
                            passengerType: e.target.checked ? "Infant" : "",
                          });
                        }}
                        value="Infant"
                      >
                        Infant
                      </Checkbox>
                    </div>
                  </div>
                  <div>
                    <small>Flight route</small>
                    <div className="flex flex-wrap gap-4 justify-between self-stretch pb-4">
                      <Checkbox
                        labelClassName="flex-1"
                        name="type"
                        checked={data?.flightRoute === "Oneway"}
                        onChange={(e) => {
                          setData({
                            ...data,
                            flightRoute: e.target.checked ? "Oneway" : "",
                          });
                        }}
                        value="Oneway"
                      >
                        Oneway
                      </Checkbox>
                      <Checkbox
                        labelClassName="flex-1 whitespace-nowrap"
                        name="type"
                        checked={data?.flightRoute === "RoundTrip"}
                        onChange={(e) => {
                          setData({
                            ...data,
                            flightRoute: e.target.checked ? "RoundTrip" : "",
                          });
                        }}
                        value="RoundTrip"
                      >
                        Round Trip
                      </Checkbox>
                      <Checkbox
                        labelClassName="flex-1 whitespace-nowrap"
                        name="type"
                        checked={data?.flightRoute === "MultiCity"}
                        onChange={(e) => {
                          setData({
                            ...data,
                            flightRoute: e.target.checked ? "MultiCity" : "",
                          });
                        }}
                        value="MultiCity"
                      >
                        Multi City
                      </Checkbox>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {/* <CitiesInput
                  label="Departure Airport"
                  size="small"
                  placeholder="e.g LHR"
                  value={data?.departureAirport || ""}
                  onChange={(val) =>
                    setData({ ...data, departureAirport: val })
                  }
                /> */}
                  {/* <div className='relative flex items-center justify-center  cursor-pointer'>
                    <div className='absolute items-center justify-center flex'>
                      <span className='bg-secondary shadow-lg rounded-full p-1 hover:rotate-180 transition-all' onClick={() => swipeLoc()}>
                        <Icon icon='mdi:exchange' className='!w-5 !h-5' />
                      </span>
                    </div>
                  </div> */}
                  {/* <CitiesInput
                  label={"Arrival Airport"}
                  size="small"
                  placeholder="e.g LOS"
                  value={data?.arrivalAirport || ""}
                  onChange={(val) =>
                    setData({ ...data, arrivalAirport: val })
                  }
                /> */}
                  <TextInput
                    select
                    label={"Arrival Time"}
                    value={data?.arrivalTime}
                    onChange={(ev) =>
                      setData({ ...data, arrivalTime: ev.target.value })
                    }
                  >
                    <MenuItem value={undefined}></MenuItem>
                    <MenuItem value="Morning">Morning</MenuItem>
                    <MenuItem value="Afternoon">Afternoon</MenuItem>
                    <MenuItem value="Evening">Evening</MenuItem>
                  </TextInput>
                </div>

                <ArrivalAirport
                  data={data?.arrivalAirports || []}
                  setData={(val) => setData({ ...data, arrivalAirports: val })}
                />
                <ExcludedArrivalAirport
                  data={data?.excludedArrivalAirports}
                  setData={(val) =>
                    setData({ ...data, excludedArrivalAirports: val })
                  }
                />

                <div className="py-4">
                  <hr />
                </div>
                <div className="flex gap-2">
                  <TextInput
                    select
                    label={"Departure Time"}
                    value={data?.departureTime}
                    onChange={(ev) =>
                      setData({ ...data, departureTime: ev.target.value })
                    }
                  >
                    <MenuItem value={undefined}></MenuItem>
                    <MenuItem value="Morning">Morning</MenuItem>
                    <MenuItem value="Afternoon">Afternoon</MenuItem>
                    <MenuItem value="Evening">Evening</MenuItem>
                  </TextInput>
                </div>

                <DepartureAirport
                  data={data?.departureAirports || []}
                  setData={(val) =>
                    setData({ ...data, departureAirports: val })
                  }
                />
                <ExcludedDepartureAirport
                  data={data?.excludedDepartureAirports}
                  setData={(val) =>
                    setData({ ...data, excludedDepartureAirports: val })
                  }
                />

                <hr />

                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 flex-wrap">
                    <TextInput
                      select
                      label={"Cabin Class"}
                      value={data?.cabinClass}
                      onChange={(ev) =>
                        setData({ ...data, cabinClass: ev.target.value })
                      }
                    >
                      <MenuItem value={undefined}></MenuItem>
                      <MenuItem value="Economy">Economy</MenuItem>
                      <MenuItem value="Business">Business</MenuItem>
                      <MenuItem value="PremiumEconomy">
                        Premium Economy
                      </MenuItem>
                      <MenuItem value="FirstClass">First Class</MenuItem>
                    </TextInput>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <MarkupStringTagsField
                      label="Ticket class (booking class letters)"
                      placeholder="e.g Y"
                      values={data?.ticketClass}
                      onChange={(next) =>
                        setData({ ...data, ticketClass: next })
                      }
                      bookingClassLetter
                    />
                    <MarkupStringTagsField
                      label="Excluded ticket class"
                      placeholder="e.g J"
                      values={data?.excludedTicketClass}
                      onChange={(next) =>
                        setData({ ...data, excludedTicketClass: next })
                      }
                      bookingClassLetter
                    />
                    <MarkupStringTagsField
                      label="Fare basis"
                      placeholder="e.g Y26LGTN"
                      values={data?.fareBasis}
                      onChange={(next) => setData({ ...data, fareBasis: next })}
                    />
                    <MarkupStringTagsField
                      label="Excluded fare basis"
                      placeholder="e.g Y26LGTN"
                      values={data?.excludedFareBasis}
                      onChange={(next) =>
                        setData({ ...data, excludedFareBasis: next })
                      }
                    />
                  </div>
                </div>
              </div>
            ) : null}

            <div className="self-start">
              <Checkbox
                value={"Append"}
                name="Append"
                checked={data?.append}
                onChange={(ev) =>
                  setData({ ...data, append: ev?.target?.checked })
                }
              >
                Append with other markups
              </Checkbox>
            </div>
          </>
        )}
        <div className="flex gap-4">
          {footer}
          <Button1 loading={loadings?.submit} type="submit">
            {update ? "Update Markup" : "Create Markup"}
          </Button1>
        </div>
      </form>
    </div>
  );
}

function DepartureAirport({ data, setData }) {
  const [departureAirport, setDepartureAirport] = useState("");

  const Tag = ({ value }) => (
    <div className="flex items-center border rounded-full text-sm border-primary/30">
      <span className="px-2 py-[2px]">{value || ""}</span>
      <span
        className="hover:text-red-400 flex-1 py-[2px] rounded-r-full cursor-pointer pr-2 inline-block h-full"
        onClick={() => handleRemove(value)}
      >
        <Icon icon="mdi:close" className="!w-4 !h-4" />
      </span>
    </div>
  );

  function handleRemove(iata) {
    setData && setData(data?.filter((val) => val !== iata));
  }

  function handleChange(val) {
    setDepartureAirport(val);
    if (val?.iata) setData && setData([...(data || []), val?.iata]);
  }

  return (
    <div className="flex flex-col">
      <div className="w-full flex items-center justify-between">
        <small>Departure Airports</small>
        <button
          onClick={() => {
            const selected = data ?? [];
            const localAirports = LocalAirports;

            const getKey = (a) =>
              (a.code ?? a.id ?? a).toString().toUpperCase();

            const selectedKeys = new Set(selected.map(getKey));

            const filteredLocalAirports = (localAirports ?? []).filter(
              (a) => !selectedKeys.has(getKey(a)),
            );
            setData?.([...(data || []), ...filteredLocalAirports]);
          }}
        >
          <small className="text-blue-400 ">+ Add Local Airports</small>
        </button>
      </div>
      <div className="flex gap-2 items-center max-w-[500px] overflow-x-auto pb-1 my-2">
        {data?.map((val, i) => (
          <Tag value={val} key={i} />
        ))}
      </div>
      <CitiesInput
        label={""}
        size="small"
        placeholder="e.g LOS"
        value={departureAirport || ""}
        onChange={(val) => handleChange(val)}
      />
    </div>
  );
}

function ExcludedDepartureAirport({ data, setData }) {
  const [excludedDepartureAirport, setExcludedDepartureAirport] = useState("");

  const Tag = ({ value }) => (
    <div className="flex items-center border rounded-full text-sm border-primary/30">
      <span className="px-2 py-[2px]">{value || ""}</span>
      <span
        className="hover:text-red-400 flex-1 py-[2px] rounded-r-full cursor-pointer pr-2 inline-block h-full"
        onClick={() => handleRemove(value)}
      >
        <Icon icon="mdi:close" className="!w-4 !h-4" />
      </span>
    </div>
  );

  function handleRemove(iata) {
    setData && setData(data?.filter((val) => val !== iata));
  }

  function handleChange(val) {
    setExcludedDepartureAirport(val);
    if (val?.iata) setData && setData([...(data || []), val?.iata]);
  }

  return (
    <div className="flex flex-col">
      <div className="w-full flex items-center justify-between">
        <small>Excluded Departure Airports</small>
        <button
          onClick={() => {
            const selected = data ?? [];
            const localAirports = LocalAirports;

            const getKey = (a) =>
              (a.code ?? a.id ?? a).toString().toUpperCase();

            const selectedKeys = new Set(selected.map(getKey));

            const filteredLocalAirports = (localAirports ?? []).filter(
              (a) => !selectedKeys.has(getKey(a)),
            );
            setData?.([...(data || []), ...filteredLocalAirports]);
          }}
        >
          <small className="text-blue-400 ">+ Add Local Airports</small>
        </button>
      </div>
      <div className="flex gap-2 items-center max-w-[500px] overflow-x-auto pb-1 my-2">
        {data?.map((val, i) => (
          <Tag value={val} key={i} />
        ))}
      </div>
      <CitiesInput
        label={""}
        size="small"
        placeholder="e.g LOS"
        value={excludedDepartureAirport || ""}
        onChange={(val) => handleChange(val)}
      />
    </div>
  );
}

function ArrivalAirport({ data, setData }) {
  const [ArrivalAirport, setArrivalAirport] = useState("");

  const Tag = ({ value }) => (
    <div className="flex items-center border rounded-full text-sm border-primary/30">
      <span className="px-2 py-[2px]">{value || ""}</span>
      <span
        className="hover:text-red-400 flex-1 py-[2px] rounded-r-full cursor-pointer pr-2 inline-block h-full"
        onClick={() => handleRemove(value)}
      >
        <Icon icon="mdi:close" className="!w-4 !h-4" />
      </span>
    </div>
  );

  function handleRemove(iata) {
    setData && setData(data?.filter((val) => val !== iata));
  }

  function handleChange(val) {
    setArrivalAirport(val);
    if (val?.iata) setData && setData([...(data || []), val?.iata]);
  }

  return (
    <div className="flex flex-col">
      <div className="w-full flex items-center justify-between">
        <small>Arrival Airports</small>
        <button
          onClick={() => {
            const selected = data ?? [];
            const localAirports = LocalAirports;

            const getKey = (a) =>
              (a.code ?? a.id ?? a).toString().toUpperCase();

            const selectedKeys = new Set(selected.map(getKey));

            const filteredLocalAirports = (localAirports ?? []).filter(
              (a) => !selectedKeys.has(getKey(a)),
            );
            setData?.([...(data || []), ...filteredLocalAirports]);
          }}
        >
          <small className="text-blue-400 ">+ Add Local Airports</small>
        </button>
      </div>
      <div className="flex gap-2 items-center max-w-[500px] overflow-x-auto pb-1 my-2">
        {data?.map((val, i) => (
          <Tag value={val} key={i} />
        ))}
      </div>
      <CitiesInput
        label={""}
        size="small"
        placeholder="e.g LOS"
        value={ArrivalAirport || ""}
        onChange={(val) => handleChange(val)}
      />
    </div>
  );
}

function ExcludedArrivalAirport({ data, setData }) {
  const [excludedArrivalAirport, setExcludedArrivalAirport] = useState("");

  const Tag = ({ value }) => (
    <div className="flex items-center border rounded-full text-sm border-primary/30">
      <span className="px-2 py-[2px]">{value || ""}</span>
      <span
        className="hover:text-red-400 flex-1 py-[2px] rounded-r-full cursor-pointer pr-2 inline-block h-full"
        onClick={() => handleRemove(value)}
      >
        <Icon icon="mdi:close" className="!w-4 !h-4" />
      </span>
    </div>
  );

  function handleRemove(iata) {
    setData && setData(data?.filter((val) => val !== iata));
  }

  function handleChange(val) {
    setExcludedArrivalAirport(val);
    if (val?.iata) setData && setData([...(data || []), val?.iata]);
  }

  return (
    <div className="flex flex-col">
      <div className="w-full flex items-center justify-between">
        <small>Excluded Arrival Airports</small>
        <button
          onClick={() => {
            const selected = data ?? [];
            const localAirports = LocalAirports;

            const getKey = (a) =>
              (a.code ?? a.id ?? a).toString().toUpperCase();

            const selectedKeys = new Set(selected.map(getKey));

            const filteredLocalAirports = (localAirports ?? []).filter(
              (a) => !selectedKeys.has(getKey(a)),
            );
            setData?.([...(data || []), ...filteredLocalAirports]);
          }}
        >
          <small className="text-blue-400 ">+ Add Local Airports</small>
        </button>
      </div>
      <div className="flex gap-2 items-center max-w-[500px] overflow-x-auto pb-1 my-2">
        {data?.map((val, i) => (
          <Tag value={val} key={i} />
        ))}
      </div>
      <CitiesInput
        label={""}
        size="small"
        placeholder="e.g LOS"
        value={excludedArrivalAirport || ""}
        onChange={(val) => handleChange(val)}
      />
    </div>
  );
}

function MarkupStringTagsField({
  label,
  placeholder,
  values,
  onChange,
  bookingClassLetter,
}) {
  const [draft, setDraft] = useState("");
  const list = normalizeStringArray(values);

  const Tag = ({ value }) => (
    <div className="flex items-center border rounded-full text-sm border-primary/30">
      <span className="px-2 py-[2px]">{value}</span>
      <span
        className="hover:text-red-400 flex-1 py-[2px] rounded-r-full cursor-pointer pr-2 inline-block h-full"
        onClick={() => onChange(list.filter((v) => v !== value))}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onChange(list.filter((v) => v !== value));
          }
        }}
      >
        <Icon icon="mdi:close" className="!w-4 !h-4" />
      </span>
    </div>
  );

  const sanitize = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return null;
    if (bookingClassLetter) {
      const c = s
        .toUpperCase()
        .replace(/[^A-Z]/g, "")
        .slice(0, 1);
      return c || null;
    }
    return s.toUpperCase();
  };

  /** Comma / semicolon / newline–separated tokens; booking class → first letter per token */
  const addBatchFromRaw = (raw) => {
    const parts = String(raw || "")
      .split(/[,;\n]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) {
      setDraft("");
      return;
    }
    const seen = new Set(list);
    const next = [...list];
    for (const part of parts) {
      const tag = sanitize(part);
      if (!tag || seen.has(tag)) continue;
      seen.add(tag);
      next.push(tag);
    }
    onChange(next);
    setDraft("");
  };

  const add = () => {
    addBatchFromRaw(draft);
  };

  return (
    <div className="flex flex-col gap-1 w-full min-w-0">
      <small>{label}</small>
      <div className="flex gap-2 flex-wrap max-w-[500px] overflow-x-auto pb-1">
        {list.map((val, i) => (
          <Tag value={val} key={`${val}-${i}`} />
        ))}
      </div>
      <div className="flex gap-2 items-end flex-wrap">
        <TextInput
          label=""
          size="small"
          placeholder={placeholder}
          value={draft}
          inputProps={
            bookingClassLetter
              ? { style: { textTransform: "uppercase" } }
              : undefined
          }
          onChange={(e) => {
            const v = e.target.value;
            setDraft(v);
          }}
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData?.getData("text") ?? "";
            addBatchFromRaw(text);
          }}
          onBlur={() => {
            if (draft.trim()) addBatchFromRaw(draft);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          className="text-blue-400 text-sm pb-2 shrink-0"
          onClick={add}
        >
          Add
        </button>
      </div>
    </div>
  );
}
